import { StateKey } from "shared/constants/states";
import {
  DeliveryMethod,
  Language,
  SPVariables,
  Template,
  TemplateFile,
  MaterialStatus,
} from "./salesEnablement";
import { LayerDataValue } from "screens/designStudio/hooks/useLayersWithData";
import type { TemplateErrorStatus } from "screens/designStudio/templates/errorStatus.utils";
import { RenderableHtmlVariables } from "screens/designStudio/hooks/useHTMLData";
import { PrintMode, PrintOptionItem } from "./printOptions";

export type AgentFieldData = {
  refAgentId: string | undefined;
  [key: string]: string | undefined;
};

type FieldsetId = string;
export type ManualField = {
  type: "manual";
  id: string;
  name: string;
  fieldsData: { id: FieldsetId; field: MktMatFieldMap }[];
};

export type AgentField = {
  type: "agent";
  id: string;
  name: string;
  agentsData: AgentFieldData[];
  templateFieldNames: string[];
};

export type ProductFieldData = {
  id: string;
  productId?: string;
  productType?: string;
  name?: string;
  description?: string;
};

export type ProductField = {
  id: string;
  name: string;
  type: "product";
  productsData: ProductFieldData[];
};

export type DisclosureField = {
  id: string;
  name: string;
  type: "disclosure";
  value: string;
};

export type AccountField = {
  type: "account";
  value: string;
  name?: string;
};

export type OptionsField = {
  type: "options";
  optionId: string;
  childOptions: Record<string, string>;
};

export type ImageField = {
  type: "image";
  spAssetId: string;
  url: string;
};

export type MktMatField =
  | string
  | AccountField
  | OptionsField
  | AgentField
  | ProductField
  | ImageField
  | ManualField
  | DisclosureField;

export type MktMatFieldMap = {
  [layerId: string]: MktMatField | undefined;
};

export type ErrorStatus = {
  status: MaterialStatus.ERROR | MaterialStatus.UNAVAILABLE;
  message: string;
};

export type MarketingMaterial = {
  id: string;
  name: string;
  materialStatus?: MaterialStatus;
  templateId: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  locations: StateKey[];
  fields?: MktMatFieldMap;
  productTypeOffer?: string[];
  language: Language;
  templateThumbnail?: string;
  materialErrorStatus?: ErrorStatus;
};

type BaseDeliveryInput = {
  template: Template;
  material: MarketingMaterial;
  variables?: SPVariables;
  deliveryMethod: DeliveryMethod;
  renderVariables?: Record<string, LayerDataValue>;
};

export type EmailDeliveryInput = Omit<
  BaseDeliveryInput,
  "renderVariables" | "material"
> & {
  deliveryMethod: "email";
  recipients?: string[];
  subject?: string;
  material: MarketingMaterialTableItem;
  renderVariables?: RenderableHtmlVariables;
};

export type EmailDeliveryResponse =
  | { status: "success"; validRecipients: string[] }
  | { status: "failed"; invalidRecipients: string[] }
  | { status: "rejected" }
  | {
      status: "partial";
      validRecipients: string[];
      invalidRecipients: string[];
    };

export type DownloadDeliveryInput = BaseDeliveryInput & {
  deliveryMethod: "download";
};

export type BatchDeliveryMarketingMaterials = {
  result: string;
};

export type SaveMarketingMaterial = {
  values: Partial<MarketingMaterial>;
  template: Template;
  material?: MarketingMaterial;
};

type MarketingMaterialTemplate = {
  templateName: Template["name"];
  templateDescription?: Template["description"];
  templateCreatedAt: Template["createdAt"];
  templateUpdatedAt: Template["lastUpdatedAt"];
  templateDeliveryMethods?: DeliveryMethod[];
  templateAudience: Template["audience"];
  templateExpirationDate: Template["expirationDate"];
  templateTags: Template["tags"];
  templateStatus: Template["status"];
  templateLocations: Template["locations"];
  templateThumbnail: string | undefined;
  templateErrorStatus: TemplateErrorStatus | undefined;
  templateCustomizable: Template["customizable"];
  templateMaterialType: Template["materialType"];
};

// This properties on the MarketingMaterialTableItem is used to filter the data
// Not to be displayed in the table
type FilterOnlyFields = {
  account: string;
  products: string[];
  templateFile: TemplateFile;
  printOption: PrintOptionItem;
};

export type MarketingMaterialTableItem = MarketingMaterial &
  MarketingMaterialTemplate &
  Partial<FilterOnlyFields>;

export type MarketingMaterialPrintOrderInput = {
  shippingData: ShippingData;
  deliveryMethod: "print";
  materialPrintOrderData: PrintDeliveryInputMaterialItem[];
};

export type PrintDeliveryInputMaterialItem = Omit<
  BaseDeliveryInput,
  "renderVariables"
> & {
  printOrderData?: MaterialPrintOrderData;
  deliveryMethod: "print";
  renderVariables?: RenderableHtmlVariables;
};

export type MaterialPrintOrderData = {
  id?: string;
  materialId: string;
  copies: number;
  name: string;
  materialName?: string;
  printMode: PrintMode;
  amount: number;
  totalWeight: number;
  shippingData?: ShippingData;
  thumbnail?: string;
  printOption: PrintOptionItem;
};

export type MarketingMaterialPrintDeliveryForm = {
  shippingData: ShippingData;
  printMode: PrintMode;
  totalPrintingAmount: number;
  totalShippingAmount: number;
  totalWeight: number;
  materials: Record<string, PrintDeliveryMaterialItem>;
};

export type ShippingData = {
  fullName: string;
  streetAddress: string;
  additionalAddress?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  email: string;
  extension?: string;
  shippingMethod?: FedexShippingMethod;
  agentNumber: string;
};

export const isValidShippingData = (
  shippingData: unknown,
): shippingData is ShippingData => {
  if (!shippingData || typeof shippingData !== "object") return false;
  const shippingDataKeys = [
    "fullName",
    "streetAddress",
    "city",
    "state",
    "zip",
    "country",
    "phone",
    "email",
  ] as (keyof ShippingData)[];

  const hasAllRequiredKeys = shippingDataKeys.every(key => key in shippingData);
  if (!hasAllRequiredKeys) return false;

  const { fullName, streetAddress, city, state, zip, country, phone, email } =
    shippingData as Record<string, unknown>;

  return Boolean(
    fullName &&
      streetAddress &&
      zip &&
      city &&
      state &&
      country &&
      phone &&
      email,
  );
};

export type PrintDeliveryMaterialItem = {
  id: string;
  name: string;
  copies: number;
  weight: number;
  templateThumbnail?: string;
  printMode: PrintMode;
  amount: number;
  printOption: PrintOptionItem;
  confirm: {
    text: boolean;
    images: boolean;
    review: boolean;
  };
};

export type MarketingMaterialPrintDeliveryRateTable = {
  id?: string;
  type?: React.ReactNode | string;
  copies?: number | string;
  subtotal?: string;
};

export type DeliveryShippingRatesInput = {
  shippingData: ShippingData;
  totalWeight: number;
  totalAmount?: number;
};

export type FedexShippingMethod = {
  serviceName: string;
  serviceType: string;
  deliveryDay: string;
  deliveryDate: string;
  transitDays?: FedexTransitDays;
  totalCharge: number;
  ratedShipmentDetails: RatedShipmentDetail;
  currency: string;
  rateType: string;
  packagingType: string;
  serviceDescription: string;
  deliveryStation: string;
};

type FedexTransitDays = {
  minimumTransitTime: string;
  description: string;
};

type RatedShipmentDetail = {
  totalDiscounts: number;
  totalBaseCharge: number;
  totalNetCharge: number;
  totalFedexCharge: number;
};
